import { useEffect, useState } from "react";
import { api } from "../api";
import { initials } from "../Layout";
import { useAuth } from "../auth";
import ClientNotesModal from "../components/ClientNotesModal";
import ConfirmTypeModal from "../components/ConfirmTypeModal";
import InvoiceProfileModal from "../components/InvoiceProfileModal";

const PWD_CHARSET =
  "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%^&*-_=+";

function generatePassword(length = 16) {
  const chars = PWD_CHARSET;
  const arr = new Uint32Array(length);
  crypto.getRandomValues(arr);
  let out = "";
  for (let i = 0; i < length; i++) {
    out += chars[arr[i] % chars.length];
  }
  return out;
}

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "employee" });
  const [err, setErr] = useState(null);
  const [pwdCopied, setPwdCopied] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);
  // Per-client invoice details (from/bill-to/bank). Super admin only, same as
  // every other invoice surface; the server enforces it.
  const [invoiceClient, setInvoiceClient] = useState(null);
  // Admin-only notes about a client. Any admin, unlike invoice details: this is
  // "how we work with them", not bank details.
  const [notesUser, setNotesUser] = useState(null);

  const canDelete = (u) => {
    if (u.id === me?.id) return false;
    if (u.is_superadmin) return false;
    if (u.role === "admin" && !me?.is_superadmin) return false;
    return true;
  };

  const generatePwd = async () => {
    const pwd = generatePassword(16);
    setForm((f) => ({ ...f, password: pwd }));
    try {
      await navigator.clipboard.writeText(pwd);
      setPwdCopied(true);
      setTimeout(() => setPwdCopied(false), 1800);
    } catch {
      // clipboard might be unavailable (e.g. non-HTTPS); password is still set in the field
    }
  };

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    try {
      await api.post("/users", form);
      setForm({ name: "", email: "", password: "", role: "employee" });
      load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to create user");
    }
  };

  const confirmDelete = async () => {
    await api.delete(`/users/${deleteUser.id}`);
    setDeleteUser(null);
    load();
  };

  return (
    <>
      <div className="page-head">
        <div className="title-block">
          <span className="eyebrow">Admin</span>
          <h1>Users</h1>
        </div>
      </div>

      <form className="card pad" onSubmit={submit} style={{ marginBottom: 16 }}>
        <div className="form-grid">
          <div>
            <label className="field">Name</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="field">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="field">Password</label>
            <div className="pwd-row">
              <input
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                spellCheck={false}
                autoComplete="new-password"
              />
              <button type="button" className="pwd-gen" onClick={generatePwd} title="Generate & copy">
                {pwdCopied ? "Copied" : "Generate"}
              </button>
            </div>
          </div>
          <div>
            <label className="field">Role</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="employee">Employee</option>
              <option value="client">Client</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          {err && <div className="full"><div className="error">{err}</div></div>}
          <div className="full" style={{ display: "flex", justifyContent: "flex-end" }}>
            <button className="primary" type="submit">+ Add user</button>
          </div>
        </div>
      </form>

      <ConfirmTypeModal
        open={deleteUser !== null}
        title="Delete this user?"
        description={
          deleteUser
            ? `${deleteUser.name} (${deleteUser.email}) will be permanently removed.`
            : ""
        }
        phrase="delete this user"
        confirmLabel="Delete user"
        danger
        onConfirm={confirmDelete}
        onCancel={() => setDeleteUser(null)}
      />

      <ClientNotesModal
        open={notesUser !== null}
        user={notesUser}
        onClose={() => setNotesUser(null)}
        onSaved={(updated) =>
          setUsers((list) => list.map((x) => (x.id === updated.id ? updated : x)))
        }
      />

      <InvoiceProfileModal
        open={invoiceClient !== null}
        client={invoiceClient}
        onClose={() => setInvoiceClient(null)}
      />

      <div className="table-wrap">
        <table className="simple">
          <thead>
            <tr>
              <th style={{ width: "32%" }}>Name</th>
              <th>Email</th>
              <th style={{ width: 140 }}>Role</th>
              <th className="col-actions wide"></th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr>
                <td colSpan={4}>
                  <div className="empty" style={{ padding: 32 }}>No users yet.</div>
                </td>
              </tr>
            )}
            {users.map((u) => (
              <tr key={u.id}>
                <td>
                  <span className="member-chip" style={{ background: "transparent", padding: 0 }}>
                    <span className="avatar">{initials(u.name)}</span>
                    <span style={{ fontWeight: 500 }}>{u.name}</span>
                  </span>
                </td>
                <td className="muted-cell">{u.email}</td>
                <td>
                  <span className="row-h" style={{ gap: 6 }}>
                    <span className={`chip role-${u.role}`}>{u.role}</span>
                    {u.is_superadmin && <span className="chip super">super</span>}
                  </span>
                </td>
                <td className="col-actions wide">
                  <div
                    className="row-h"
                    style={{ gap: 6, justifyContent: "flex-end", flexWrap: "nowrap" }}
                  >
                    {u.role === "client" && (
                      <button
                        className={`ghost${u.notes ? " has-notes" : ""}`}
                        title={
                          u.notes
                            ? "Notes about this client (they never see them)"
                            : "Keep a note about this client (they never see it)"
                        }
                        onClick={() => setNotesUser(u)}
                      >
                        Notes{u.notes ? "" : "…"}
                      </button>
                    )}
                    {u.role === "client" && me?.is_superadmin && (
                      <button
                        className="ghost"
                        title="Invoice details used when billing this client"
                        onClick={() => setInvoiceClient(u)}
                      >
                        Invoice details…
                      </button>
                    )}
                    {canDelete(u) && (
                      <button className="ghost icon" title="Delete user" onClick={() => setDeleteUser(u)}>×</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
